/* eslint-disable react-hooks/rules-of-hooks */
import React, { useState, useEffect } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Calendar,
  PieChart as PieChartIcon,
  BarChart2,
  RefreshCw,
  AlertCircle,
  FileText,
} from "lucide-react";

const FinanceReportTab = ({
  selectedTab,
  rooms,
  staff,
  facilities,
  money,
  day,
  guests,
}) => {
  // Skip rendering if not selected
  if (selectedTab !== "finance") return null;

  // State for financial data
  const [financialData, setFinancialData] = useState([]);
  const [incomeBreakdown, setIncomeBreakdown] = useState([]);
  const [expenseBreakdown, setExpenseBreakdown] = useState([]);
  const [timeRange, setTimeRange] = useState("week"); // "week", "month", "all"
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  
  // Calculate daily financial metrics when component renders
  useEffect(() => {
    calculateFinances();
  }, [day, rooms, staff, facilities, guests]);
  
  // Generate fake historical data if the financial data is empty
  useEffect(() => {
    if (financialData.length === 0 && day > 1) {
      generateHistoricalData();
    }
  }, []);
  
  // Calculate current financial metrics
  const calculateFinances = () => {
    // Calculate daily room income
    const roomIncome = rooms.reduce((total, room) => {
      return total + (room.occupied ? room.price : 0);
    }, 0);
    
    // Calculate facility income (estimated)
    let facilityIncome = 0;
    facilities.forEach(facility => {
      if (facility.name === "Lobby") return;
      
      // Simple estimate based on guest count and facility level
      const guestCount = guests.length;
      const usageRate = Math.min(0.3 + (guestCount / 20), 0.8);
      
      // Find the facility type to get base income
      const facilityType = getFacilityType(facility.name);
      if (facilityType) {
        facilityIncome += Math.round(
          guestCount * usageRate * facilityType.incomePerGuest * facility.level
        );
      }
    });
    
    // Staff expenses
    const staffExpenses = staff.reduce((total, employee) => {
      return total + employee.salary;
    }, 0);
    
    // Facility maintenance
    const facilityExpenses = facilities.reduce((total, facility) => {
      return total + facility.upkeep;
    }, 0);
    
    // Room maintenance
    const roomExpenses = rooms.reduce((total, room) => {
      // Approximate room upkeep based on type
      const roomUpkeep = getRoomUpkeep(room.type);
      return total + roomUpkeep;
    }, 0);
    
    // Calculate total income, expenses, and profit
    const totalIncome = roomIncome + facilityIncome;
    const totalExpenses = staffExpenses + facilityExpenses + roomExpenses;
    const profit = totalIncome - totalExpenses;
    
    // Add to financial data history
    const newFinancialEntry = {
      day,
      totalIncome,
      roomIncome,
      facilityIncome,
      totalExpenses,
      staffExpenses,
      facilityExpenses,
      roomExpenses,
      profit,
      cash: money,
    };
    
    setFinancialData(prev => {
      // Ensure we don't duplicate entries for the same day
      const filtered = prev.filter(entry => entry.day !== day);
      return [...filtered, newFinancialEntry].sort((a, b) => a.day - b.day);
    });
    
    // Update income breakdown
    setIncomeBreakdown([
      { name: "Rooms", value: roomIncome },
      { name: "Facilities", value: facilityIncome },
    ]);
    
    // Update expense breakdown
    setExpenseBreakdown([
      { name: "Staff", value: staffExpenses },
      { name: "Facilities", value: facilityExpenses },
      { name: "Rooms", value: roomExpenses },
    ]);
  };
  
  // Helper function to get room upkeep cost
  const getRoomUpkeep = (roomType) => {
    // These should match the values in roomTypes in the main component
    switch (roomType) {
      case "Standard": return 20;
      case "Deluxe": return 35;
      case "Suite": return 60;
      case "Executive": return 80;
      case "Penthouse": return 120;
      default: return 20;
    }
  };
  
  const getFacilityType = (facilityName) => {
    // These should match the values in facilityTypes in the main component
    const facilityData = {
      "Restaurant": { incomePerGuest: 40, upkeep: 200 },
      "Bar": { incomePerGuest: 100, upkeep: 150 },
      "Spa": { incomePerGuest: 60, upkeep: 300 },
      "Gym": { incomePerGuest: 15, upkeep: 100 },
      "Pool": { incomePerGuest: 30, upkeep: 250 },
      "Conference Room": { incomePerGuest: 50, upkeep: 180 },
      "Gift Shop": { incomePerGuest: 15, upkeep: 80 },
      "Casino": { incomePerGuest: 80, upkeep: 400 },
      "Nightclub": { incomePerGuest: 70, upkeep: 350 },
      // Added missing facility types from the expanded list
      "Rooftop Garden": { incomePerGuest: 10, upkeep: 120 },
      "Business Center": { incomePerGuest: 20, upkeep: 90 },
      "Kids Club": { incomePerGuest: 25, upkeep: 140 },
      "Art Gallery": { incomePerGuest: 35, upkeep: 110 },
      "Pet Spa": { incomePerGuest: 40, upkeep: 130 },
      "Tennis Court": { incomePerGuest: 25, upkeep: 200 },
      "Cigar Lounge": { incomePerGuest: 50, upkeep: 160 },
      "Movie Theater": { incomePerGuest: 35, upkeep: 220 },
      "Library": { incomePerGuest: 5, upkeep: 60 }
    };
      
    return facilityData[facilityName];
  };
  
  // Generate historical financial data for new games
  const generateHistoricalData = () => {
    setIsGeneratingReport(true);
    
    // Create historical data based on current metrics with some randomness
    const currentRoomIncome = rooms.reduce((total, room) => {
      return total + (room.occupied ? room.price : 0);
    }, 0);
    
    const currentStaffExpenses = staff.reduce((total, employee) => {
      return total + employee.salary;
    }, 0);
    
    const currentFacilityExpenses = facilities.reduce((total, facility) => {
      return total + facility.upkeep;
    }, 0);
    
    // Generate data for the past 30 days
    const historicalData = [];
    for (let i = Math.max(1, day - 30); i < day; i++) {
      // Add random variation to financial metrics
      const variation = () => 0.8 + Math.random() * 0.4; // 80% to 120% of base value
      
      const roomIncome = Math.round(currentRoomIncome * variation());
      const facilityIncome = Math.round((currentRoomIncome * 0.4) * variation());
      const staffExpenses = Math.round(currentStaffExpenses * variation());
      const facilityExpenses = Math.round(currentFacilityExpenses * variation());
      const roomExpenses = Math.round((rooms.length * 20) * variation());
      
      const totalIncome = roomIncome + facilityIncome;
      const totalExpenses = staffExpenses + facilityExpenses + roomExpenses;
      const profit = totalIncome - totalExpenses;
      
      // Calculate cash based on previous day's cash and current profit
      const prevCash = i > 1 && historicalData.length > 0 ? 
        historicalData[historicalData.length - 1].cash : money - profit;
      const cash = prevCash + profit;
      
      historicalData.push({
        day: i,
        totalIncome,
        roomIncome,
        facilityIncome,
        totalExpenses,
        staffExpenses,
        facilityExpenses,
        roomExpenses,
        profit,
        cash,
      });
    }
    
    setFinancialData(historicalData);
    setTimeout(() => setIsGeneratingReport(false), 800);
  };
  
  // Filter data based on selected time range
  const getFilteredData = () => {
    if (financialData.length === 0) return [];
    
    const currentDay = day;
    switch (timeRange) {
      case "week":
        return financialData.filter(item => item.day > currentDay - 7);
      case "month":
        return financialData.filter(item => item.day > currentDay - 30);
      case "all":
      default:
        return financialData;
    }
  };
  
  // Helper to format money
  const formatMoney = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  // Helper to calculate profit trends
  const calculateProfitTrend = () => {
    const filteredData = getFilteredData();
    if (filteredData.length < 2) return { trend: 0, percentage: 0 };
    
    const lastWeekData = filteredData.slice(-7);
    if (lastWeekData.length < 2) return { trend: 0, percentage: 0 };
    
    const oldAvgProfit = lastWeekData.slice(0, Math.ceil(lastWeekData.length / 2))
      .reduce((sum, item) => sum + item.profit, 0) / Math.ceil(lastWeekData.length / 2);
      
    const newAvgProfit = lastWeekData.slice(-Math.floor(lastWeekData.length / 2))
      .reduce((sum, item) => sum + item.profit, 0) / Math.floor(lastWeekData.length / 2);
      
    const change = newAvgProfit - oldAvgProfit;
    const percentage = oldAvgProfit !== 0 ? (change / Math.abs(oldAvgProfit)) * 100 : 0;
    
    return {
      trend: change > 0 ? 1 : change < 0 ? -1 : 0,
      percentage: Math.abs(Math.round(percentage)),
    };
  };
  
  // Calculate summary metrics
  const calculateSummary = () => {
    const filteredData = getFilteredData();
    if (filteredData.length === 0) return { totalProfit: 0, avgProfit: 0, bestDay: 0, worstDay: 0 };
    
    const totalProfit = filteredData.reduce((sum, item) => sum + item.profit, 0);
    const avgProfit = Math.round(totalProfit / filteredData.length);
    
    let bestDay = filteredData[0].day;
    let bestProfit = filteredData[0].profit;
    let worstDay = filteredData[0].day;
    let worstProfit = filteredData[0].profit;
    
    filteredData.forEach(item => {
      if (item.profit > bestProfit) {
        bestProfit = item.profit;
        bestDay = item.day;
      }
      if (item.profit < worstProfit) {
        worstProfit = item.profit;
        worstDay = item.day;
      }
    });
    
    return { totalProfit, avgProfit, bestDay, worstDay };
  };
  
  // Get color codes for the pie charts
  const INCOME_COLORS = ['#2563eb', '#4ade80'];
  const EXPENSE_COLORS = ['#ef4444', '#fb923c', '#facc15'];
  
  // Prepare summary data
  const summary = calculateSummary();
  const profitTrend = calculateProfitTrend();
  const filteredData = getFilteredData();
  
  // Calculate occupancy rate for revenue metrics
  const occupancyRate = rooms.length > 0
    ? Math.round((rooms.filter(r => r.occupied).length / rooms.length) * 100)
    : 0;
  
  // Calculate revenue per available room (RevPAR)
  const revPAR = rooms.length > 0
    ? Math.round(rooms.reduce((total, room) => total + (room.occupied ? room.price : 0), 0) / rooms.length)
    : 0;
  
  return (
<div>
  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
    <h2 className="text-xl font-bold flex items-center">
      <DollarSign className="mr-2 text-green-600" /> Financial Reports
    </h2>
    <div className="flex flex-wrap w-full sm:w-auto gap-2">
      <select 
        value={timeRange}
        onChange={(e) => setTimeRange(e.target.value)}
        className="px-3 py-1 border rounded-lg text-sm bg-white flex-grow sm:flex-grow-0"
      >
        <option value="week">Last 7 days</option>
        <option value="month">Last 30 days</option>
        <option value="all">All time</option>
      </select>
      
      <button 
        onClick={generateHistoricalData}
        disabled={isGeneratingReport}
        className="flex items-center justify-center px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex-grow sm:flex-grow-0"
      >
        {isGeneratingReport ? (
          <>
            <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
            Generating...
          </>
        ) : (
          <>
            <RefreshCw className="w-4 h-4 mr-1" />
            Refresh
          </>
        )}
      </button>
    </div>
  </div>
  
  {/* Financial summary cards */}
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
    {/* Profit card */}
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <div className="flex justify-between items-center">
        <div>
          <p className="text-sm text-gray-500">Total Profit ({timeRange === "week" ? "7" : timeRange === "month" ? "30" : "All"} days)</p>
          <p className="text-2xl font-bold text-gray-800">{formatMoney(summary.totalProfit)}</p>
        </div>
        <div className={`rounded-full p-2 ${summary.totalProfit >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
          {summary.totalProfit >= 0 ? 
            <TrendingUp className="w-5 h-5 text-green-600" /> : 
            <TrendingDown className="w-5 h-5 text-red-600" />
          }
        </div>
      </div>
      <div className="mt-2 flex items-center">
        {profitTrend.trend > 0 ? (
          <span className="text-xs text-green-600 flex items-center">
            <TrendingUp className="w-3 h-3 mr-1" /> Up {profitTrend.percentage}% from previous period
          </span>
        ) : profitTrend.trend < 0 ? (
          <span className="text-xs text-red-600 flex items-center">
            <TrendingDown className="w-3 h-3 mr-1" /> Down {profitTrend.percentage}% from previous period
          </span>
        ) : (
          <span className="text-xs text-gray-500">No significant change</span>
        )}
      </div>
    </div>
    
    {/* Average daily profit */}
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <p className="text-sm text-gray-500">Avg. Daily Profit</p>
      <p className="text-2xl font-bold text-gray-800">{formatMoney(summary.avgProfit)}</p>
      <div className="mt-2 text-xs text-gray-600">
        <div className="flex flex-col sm:flex-row sm:justify-between">
          <span>Best day: Day {summary.bestDay}</span>
          <span>Worst day: Day {summary.worstDay}</span>
        </div>
      </div>
    </div>
    
    {/* Occupancy metrics */}
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <p className="text-sm text-gray-500">Occupancy Rate</p>
      <p className="text-2xl font-bold text-gray-800">{occupancyRate}%</p>
      <div className="mt-2 text-xs text-gray-600">
        <span>Occupied: {rooms.filter(r => r.occupied).length}/{rooms.length} rooms</span>
      </div>
    </div>
    
    {/* RevPAR - Revenue Per Available Room */}
    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
      <p className="text-sm text-gray-500">RevPAR</p>
      <p className="text-2xl font-bold text-gray-800">{formatMoney(revPAR)}</p>
      <div className="mt-2 text-xs text-gray-600">
        <span>Revenue Per Available Room</span>
      </div>
    </div>
  </div>
  
  {financialData.length === 0 ? (
    <div className="bg-gray-50 p-8 rounded-lg text-center text-gray-500 mb-6 border border-gray-200">
      <AlertCircle className="w-10 h-10 mx-auto mb-3 text-gray-400" />
      <p className="mb-2">No financial data available yet.</p>
      <p className="text-sm">Data will be collected as you play the game.</p>
    </div>
  ) : (
    <>
      {/* Main chart: Profit over time */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
        <h3 className="font-bold mb-3 flex items-center">
          <BarChart2 className="w-4 h-4 mr-2 text-blue-600" />
          Daily Profit Trends
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" label={{ value: 'Day', position: 'insideBottomRight', offset: -5 }} />
              <YAxis 
                tickFormatter={(value) => `$${value}`}
                label={{ value: 'Profit', angle: -90, position: 'insideLeft' }}
              />
              <Tooltip 
                formatter={(value) => [`$${value}`, 'Profit']}
                labelFormatter={(value) => `Day ${value}`}
              />
              <Line 
                type="monotone" 
                dataKey="profit" 
                stroke="#2563eb" 
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      
      {/* Income and expense charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Income breakdown */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="font-bold mb-3 flex items-center">
            <PieChartIcon className="w-4 h-4 mr-2 text-green-600" />
            Income Breakdown
          </h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={incomeBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {incomeBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={INCOME_COLORS[index % INCOME_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {incomeBreakdown.map((item, index) => (
              <div key={item.name} className="flex items-center">
                <div className="w-3 h-3 mr-1 rounded-full" style={{ backgroundColor: INCOME_COLORS[index % INCOME_COLORS.length] }}></div>
                <span>{item.name}: {formatMoney(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Expense breakdown */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
          <h3 className="font-bold mb-3 flex items-center">
            <PieChartIcon className="w-4 h-4 mr-2 text-red-600" />
            Expense Breakdown
          </h3>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={expenseBreakdown}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {expenseBreakdown.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={EXPENSE_COLORS[index % EXPENSE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => [`$${value}`, 'Amount']} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            {expenseBreakdown.map((item, index) => (
              <div key={item.name} className="flex items-center">
                <div className="w-3 h-3 mr-1 rounded-full" style={{ backgroundColor: EXPENSE_COLORS[index % EXPENSE_COLORS.length] }}></div>
                <span>{item.name}: {formatMoney(item.value)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Income vs Expense comparison chart */}
      <div className="bg-white p-4 rounded-lg shadow-sm mb-6 border border-gray-200">
        <h3 className="font-bold mb-3 flex items-center">
          <BarChart2 className="w-4 h-4 mr-2 text-purple-600" />
          Income vs Expenses
        </h3>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={filteredData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="day" label={{ value: 'Day', position: 'insideBottomRight', offset: -5 }} />
              <YAxis 
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip 
                formatter={(value) => [`$${value}`, 'Amount']}
                labelFormatter={(value) => `Day ${value}`}
              />
              <Legend />
              <Bar dataKey="totalIncome" fill="#2563eb" name="Income" />
              <Bar dataKey="totalExpenses" fill="#ef4444" name="Expenses" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </>
  )}
  
  {/* Financial tips */}
  <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
    <h3 className="font-bold mb-2 flex items-center">
      <FileText className="w-4 h-4 mr-2 text-blue-600" />
      Financial Management Tips
    </h3>
    <ul className="text-sm space-y-1 pl-6 list-disc">
      <li>Keep your occupancy rate above 70% for optimal profitability</li>
      <li>Balance room prices to maximize revenue while maintaining bookings</li>
      <li>High-quality rooms and facilities can command higher prices</li>
      <li>Staff costs are usually your highest expense - hire strategically</li>
      <li>Consider seasonal demand when planning expansions or renovations</li>
      <li>Monitor your RevPAR (Revenue Per Available Room) to gauge efficiency</li>
    </ul>
  </div>
</div>
  );
};

export default FinanceReportTab;